import { FC, ReactNode, RefObject } from 'react';
import {
  Badge,
  Flex,
  Link,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { isEmpty } from 'ramda';

import {
  DownloadFileQueryVariables,
  FileBrowserStorageObjectFragment,
} from '../../graphql/generated';
import { isFile } from '../../graphql/utils/utils';
import { EnhancedFileSize } from '../file-browser/file-size';
import { FileStatus } from '../file-browser/file-status/file-status';
import { Name } from '../file-browser/name';
import { FileDownloadIconButton } from '../file-download-button/file-download-button';
import { UpdatedAt } from './updated-at';

export type SearchResultsListProps = {
  EmptyState: ReactNode;
  files: FileBrowserStorageObjectFragment[];
  downloadRef: RefObject<HTMLAnchorElement>;
  onDownload: (file: DownloadFileQueryVariables) => void;
};

export const Placeholder: FC = ({ ...props }) => <span {...props}>--</span>;

export const SearchResultsList: FC<SearchResultsListProps> = ({
  files,
  onDownload,
  downloadRef,
  EmptyState,
  ...rest
}) => {
  const handleDownload = (
    file: FileBrowserStorageObjectFragment
  ) => async () => {
    onDownload({
      cedant: file.cedant,
      fileId: file.id,
    });
  };

  return (
    <>
      <Flex flex={1} direction="column" mt={8} {...rest}>
        <Table variant="file-browser" data-testid="search-results">
          <Thead>
            <Tr>
              <Th colSpan={4}>Name</Th>
              <Th>Treaty</Th>
              <Th>Status</Th>
              <Th>Uploaded</Th>
              <Th>Size</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {files.map((object) => {
              return (
                <Tr key={object.id}>
                  <Td colSpan={4} data-testid="file-name">
                    <Name object={object} enableTooltip />
                  </Td>
                  <Td data-testid="file-status">
                    <Badge>{object.cedant}</Badge>
                  </Td>
                  <Td data-testid="file-status">
                    <FileStatus object={object} />
                  </Td>
                  <Td data-testid="file-updated-at">
                    <UpdatedAt object={object} />
                  </Td>
                  <Td data-testid="file-size">
                    <EnhancedFileSize object={object} />
                  </Td>
                  {isFile(object) && (
                    <Td data-testid="file-actions" textAlign="right">
                      <FileDownloadIconButton
                        file={object}
                        onDownload={handleDownload(object)}
                      />
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        <br />
        {isEmpty(files) && EmptyState}
      </Flex>
      <Link
        download
        display="none"
        ref={downloadRef}
        data-testid="signed-url-link"
      />
    </>
  );
};
